import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import NewCustomButton from './common/NewCustomButton';
import { 
  FaArchive, 
  FaTrash, 
  FaUndo,
  FaExclamationTriangle,
  FaArrowLeft
} from 'react-icons/fa';

import { collection, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatDate } from '../utils/dateUtils';
import { logAuditEvent } from '../firebase/auditService';
import '../styles/ArchivedProperties.css';

const ArchivedTenants = () => {
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const { fontSize, lineHeight, letterSpacing, fontWeight } = useAccessibility();
  const [archivedTenants, setArchivedTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchArchivedTenants();
  }, [currentUser]);

  const fetchArchivedTenants = async () => {
    try {
      setLoading(true);
      const tenantsRef = collection(db, 'tenants');
      const q = query(tenantsRef, where('archived', '==', true));
      const querySnapshot = await getDocs(q);
      
      const tenants = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setArchivedTenants(tenants);
    } catch (err) {
      console.error('Error fetching archived tenants:', err);
      setError('Failed to load archived tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (tenantId) => {
    try {
      // Get tenant data before unarchiving
      const tenantDoc = doc(db, 'tenants', tenantId);
      const tenantSnapshot = await getDoc(tenantDoc);
      
      if (!tenantSnapshot.exists()) {
        throw new Error('Tenant not found');
      }
      
      const tenantData = tenantSnapshot.data();
      
      // Update the tenant document
      await updateDoc(tenantDoc, {
        archived: false,
        archivedDate: null
      });
      
      // Prepare audit log data, ensuring no undefined values
      const auditData = {
        eventType: 'tenant',
        description: `Tenant unarchived: ${tenantData?.firstName || ''} ${tenantData?.lastName || ''}`,
        tenantId: tenantId,
        tenantName: `${tenantData?.firstName || ''} ${tenantData?.lastName || ''}`,
        tenantEmail: tenantData?.email || ''
      };
      
      // Only add property fields if they exist and are not undefined
      if (tenantData?.propertyId) {
        auditData.propertyId = tenantData.propertyId;
      }
      
      if (tenantData?.propertyAddress) {
        auditData.propertyAddress = tenantData.propertyAddress;
      }
      
      // Log the unarchive action to the main audit logs
      await logAuditEvent(auditData);
      
      // Refresh the list
      await fetchArchivedTenants();
    } catch (err) {
      console.error('Error unarchiving tenant:', err);
      setError('Failed to unarchive tenant');
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>
      <Typography sx={{
        fontSize: fontSize,
        lineHeight: lineHeight,
        letterSpacing: letterSpacing,
        fontWeight: fontWeight,
        color: isDarkMode ? '#fff' : '#1a1a1a'
      }}>Loading...</Typography>
    </Box>;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error"
          sx={{
            '& .MuiAlert-message': {
              fontSize: fontSize,
              lineHeight: lineHeight,
              letterSpacing: letterSpacing,
              fontWeight: fontWeight
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <div className="archived-properties-container" role="main" aria-label="Archived Tenants">
      <div className="archived-properties-header">
        <NewCustomButton
          onClick={() => navigate('/tenants')}
          variant="outlined"
          sx={{ 
            mb: 2,
            borderRadius: '9999px',
            textTransform: 'none',
            fontWeight: 600,
            height: '40px',
            padding: '8px 16px',
            minWidth: '140px'
          }}
        >
          Back to Tenants
        </NewCustomButton>
        <h1>Archived Tenants</h1>
      </div>

      {archivedTenants.length === 0 ? (
        <Alert 
          severity="info"
          sx={{
            '& .MuiAlert-message': {
              fontSize: fontSize,
              lineHeight: lineHeight,
              letterSpacing: letterSpacing,
              fontWeight: fontWeight
            }
          }}
        >
          No archived tenants found
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {archivedTenants.map((tenant) => (
            <Grid item xs={12} key={tenant.id}>
              <div className="archived-property-item" role="article">
                <div className="property-content">
                  <Box>
                    <div className="property-address">
                      {tenant.firstName} {tenant.lastName}
                    </div>
                    <div className="archive-date">
                      Archived on: {formatDate(tenant.archivedDate)}
                    </div>
                  </Box>
                  <Box>
                    <Tooltip title="Unarchive">
                      <IconButton 
                        onClick={() => handleUnarchive(tenant.id)}
                        className="unarchive-button"
                        aria-label="Unarchive tenant"
                      >
                        <FaUndo />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default ArchivedTenants;